import ModalInfo from 'components/shared/ModalInfo'
import { FormattedMessage, useIntl } from 'react-intl'
import Text from 'components/shared/Text'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { useTickets } from 'store/ticket-slice/hooks'
import { useTransport } from 'store/transport-slice/hooks'
import { useCabana } from 'store/cabana-slice/hooks'
import { useUpsell } from 'store/upsell-slice/hooks'
import { useApp } from 'store/steps'
import { useAccommodation } from 'store/accommodation-slice/hooks'
import {
    removeAccommodationFromStore,
    setAccommodationPromotionApplicable,
} from 'store/accommodation-slice'
import { useSpaCart } from 'routes/Upsell/SpaCreator/hooks/useSpaCart'
import { useCityCart } from 'components/common/CityBreak/utils/useCityCart'
import { setIsErrorModalOpened } from 'store/general-slice'
interface ModalHeadingProps {
    isOpen: boolean
    onReset: () => void
    nextStep: () => void
}

export const SkipStepModal = ({
    isOpen,
    onReset,
    nextStep,
}: ModalHeadingProps) => {
    const { formatMessage } = useIntl()
    const { stepId, isCreatorAccommodation } = useApp()
    const dispatch = useAppDispatch()
    const { removeAllTicketsFromCart, isTicketProcessing } = useTickets()
    const { deleteItemsFromCart: removeCityBreakTicketsFromCart } =
        useCityCart()
    const {
        deleteSpaFromCart,
        isDeleteSpaLoading,
        cartItems: spaCartItems,
    } = useSpaCart()
    const { removeTransportFromCart, isTransportProcessing } = useTransport(0)
    const { removeCabanasFromCart, areCabanasProcessing } = useCabana(null)
    const { removeAllUpsellDataFromCart, isUpsellProcessing } = useUpsell({})
    const { removeAccommodationFromCart, isAccommodationDeleting } =
        useAccommodation()

    const cartItems = useAppSelector((state) => state.cart.items)

    const { busTickets, cabanas, memoryCapture, accommodation } = cartItems
    const isSpaEmpty = !spaCartItems.length
    const isTransportEmpty = busTickets.length === 0
    const isCabanaEmpty = cabanas.length === 0
    const isUpsellEmpty = (memoryCapture?.quantity || 0) === 0
    const isAccommodationEmpty = !accommodation?.cartItemId

    const isLoading =
        isDeleteSpaLoading ||
        isTicketProcessing ||
        isTransportProcessing ||
        areCabanasProcessing ||
        isUpsellProcessing ||
        isAccommodationDeleting

    // TODO: consider unifying with clearing items from cart when going back on steps (removeNextStepsFromCart())
    const handleAccept = async () => {
        const deleteActions: Promise<unknown>[] = []

        switch (stepId) {
            case 'tickets':
                try {
                    await removeCityBreakTicketsFromCart()
                    await removeAllTicketsFromCart()
                    await removeTransportFromCart()
                    nextStep()
                } catch (errors) {
                    // just reset session in case of deleting errors
                    dispatch(setIsErrorModalOpened(true))
                }
                break
            case 'upsell':
                if (!isSpaEmpty) {
                    deleteActions.push(deleteSpaFromCart())
                }
                if (!isTransportEmpty) {
                    deleteActions.push(removeTransportFromCart())
                }
                if (!isCabanaEmpty) {
                    deleteActions.push(removeCabanasFromCart())
                }
                if (!isUpsellEmpty) {
                    deleteActions.push(removeAllUpsellDataFromCart())
                }

                // TODO: modify deleting items when skipping steps to catch errors - single or global
                // eslint-disable-next-line compat/compat
                Promise.all(deleteActions).then(() => nextStep())
                break
            case 'accommodation_details':
            case 'accommodation':
                dispatch(removeAccommodationFromStore())
                dispatch(setAccommodationPromotionApplicable(false))
                try {
                    !isAccommodationEmpty &&
                        (await removeAccommodationFromCart({
                            // unlike in accommodation creator, in standard creator
                            // it is not needed to refetch cart after skipping accommodation step (=== deleting accommodation),
                            // it's because the next step is checkout - which in default refetches cart on its initial render
                            shouldSkipRefetch: !isCreatorAccommodation,
                        }))
                    nextStep()
                } catch (errors) {
                    // just reset session in case of deleting errors
                    dispatch(setIsErrorModalOpened(true))
                }
                break
            default:
                nextStep()
        }
    }

    return (
        <ModalInfo
            isOpened={isOpen}
            linkLabel={formatMessage({
                id: 'global.btn.dont_skip_step',
                defaultMessage: 'Nie pomijaj',
            })}
            buttonLabel={formatMessage({
                id: 'global.btn.skip',
                defaultMessage: 'Pomiń',
            })}
            onButtonClick={handleAccept}
            onLinkClick={onReset}
            isLoading={isLoading}
            variant="secondary"
            testIdPrefix="skip-step-modal"
        >
            <Text variant="heading3">
                <FormattedMessage
                    id="global.modal.skip_step_title"
                    defaultMessage="Czy na pewno chcesz pominąć ten krok?"
                />
            </Text>
            <Text variant="body" mt={16} color="grayscale_900">
                <FormattedMessage
                    id="global.modal.skip_step_description"
                    defaultMessage="Produkty i usługi dodane w tym kroku zostaną usunięte z zamówienia."
                />
            </Text>
        </ModalInfo>
    )
}
